<template>
  <q-dialog
    v-model="shown"
    data-test-id="manage-links-dialog"
  >
    <q-card
      style="max-width: 100vw; width: 700px; display: flex; flex-direction: column;"
      :style="{ 'max-height': $q.screen.gt.sm ? '80vh' : '100vh' }"
      :loading="loading"
    >
      <q-inner-loading
        style="z-index: 1000"
        :showing="loading"
      >
        <q-spinner size="xl" />
        <div v-if="linkingDestinationTotal !== 0 && !$_.isEmpty(linkingDestinationName)">
          Linking to {{ linkingDestinationName }} ({{ linkingDestinationIndex }}/{{ linkingDestinationTotal }})
        </div>
      </q-inner-loading>
      <q-card-section class="row items-center q-mx-xs flex-shrink-0">
        <div class="text-h6">
          Manage {{ model.humanTypeName }} Links
        </div>
        <q-space />
        <q-btn
          icon="refresh"
          flat
          round
          dense
          @click="refresh"
        />
        <q-btn
          v-close-popup
          icon="close"
          flat
          round
          dense
        />
      </q-card-section>
      <q-card-section class="q-py-none">
        <q-item-label
          class="text-body-em text-grey-8 q-mb-sm"
          style="font-size: 14px; font-weight: normal;"
        >
          Action
        </q-item-label>
        <manage-links-actions-select
          v-model="action"
          :model="model"
          :loading="loading"
        />
      </q-card-section>
      <q-card-section class="flex-grow-1 overflow-auto q-pr-none">
        <q-item
          v-if="circuitsSitesDescendants.length > 1"
          dense
        >
          <q-item-section>
            <q-checkbox
              :model-value="selectedAll"
              label="Select All"
              @update:model-value="_ => selectAll()"
            />
          </q-item-section>
        </q-item>
        <q-expansion-item
          v-for="circuit in circuitsSitesDescendants"
          :key="circuit.id"
          :model-value="expandedCircuitsByIds[circuit.id]"
          class="full-width"
          expand-icon-class="q-pr-none"
          :header-class="circuitsSitesDescendants.length === 1 ? 'q-pl-none' : undefined"
          :clickable="circuitsSitesDescendants.length > 1"
          dense
          :hide-expand-icon="circuitsSitesDescendants.length === 1 || (model.level !== MODEL_LEVEL.SITE && $_.first(circuit.sites).descendants.length <= 1)"
          :expand-separator="false"
          @update:model-value="value => { if (circuitsSitesDescendants.length > 1) { expandedCircuitsByIds[circuit.id] = value } }"
        >
          <template
            #header
          >
            <link-to-circuit-item
              v-if="circuitsSitesDescendants.length > 1"
              :model-value="selectedDestinationsByIds[circuit.id].selected"
              :circuit="circuit"
              :selectable="circuitSelectable(circuit)"
              :versioned="model.versioned"
              :model-level="model.level"
              :is-delete-permitted="model.isDeletePermitted"
              @archive="descendant => $refs.archiveConfirmationDialog.show(model.typeName, descendant.id)"
              @unarchive="unarchiveDescendant"
              @show-history="descendant => $refs.versionsDialog.show(model.typeName, descendant.id)"
              @update:model-value="value => toggleCircuit(circuit, value)"
            />
            <q-item
              v-else
              dense
            >
              <q-item-section>
                <q-checkbox
                  :model-value="selectedAll"
                  label="Select All"
                  @update:model-value="_ => selectAll()"
                />
              </q-item-section>
            </q-item>
          </template>
          <q-list
            :class="circuitsSitesDescendants.length > 1 ? 'q-ml-md' : undefined"
          >
            <template
              v-for="site in circuit.sites"
              :key="site.id"
            >
              <link-to-site-item
                v-if="model.level === MODEL_LEVEL.SITE"
                :model-value="selectedDestinationsByIds[circuit.id].sites[site.id]"
                :site="site"
                :selectable="siteSelectable(site)"
                :versioned="model.versioned"
                :is-delete-permitted="model.isDeletePermitted"
                @archive="descendant => $refs.archiveConfirmationDialog.show(model.typeName, descendant.id)"
                @unarchive="unarchiveDescendant"
                @show-history="descendant => $refs.versionsDialog.show(model.typeName, descendant.id)"
                @update:model-value="value => toggleSite(site, value)"
              />
              <template
                v-if="site.descendants.length > 1"
              >
                <link-to-model-item
                  v-for="descendant in site.descendants"
                  :key="descendant.id"
                  :model-value="selectedDescendantsByIds[circuit.id][site.id][descendant.id]"
                  :class="circuitsSitesDescendants.length > 1 || model.level === MODEL_LEVEL.SITE ? 'q-ml-md' : undefined"
                  :descendant="descendant"
                  :selectable="descendantSelectable(descendant)"
                  :versioned="model.versioned"
                  :is-delete-permitted="model.isDeletePermitted"
                  @archive="$refs.archiveConfirmationDialog.show(model.typeName, descendant.id)"
                  @unarchive="unarchiveDescendant"
                  @show-history="$refs.versionsDialog.show(model.typeName, descendant.id)"
                  @update:model-value="value => toggleDescendant(descendant, value)"
                />
              </template>
            </template>
          </q-list>
        </q-expansion-item>
      </q-card-section>
      <q-card-actions class="row justify-end q-ma-sm flex-shrink-0">
        <q-btn
          class="text-bold link-action-button"
          label="Cancel"
          color="white"
          text-color="black"
          dense
          no-caps
          :unelevated="false"
          :loading="loading"
          @click="hide"
        />
        <q-btn
          class="text-bold link-action-button"
          :label="actionLabel"
          color="primary"
          :text-color="$isColorDark(currentSite.primaryColor) ? 'white' : 'black'"
          dense
          no-caps
          :unelevated="false"
          :disable="selectedModelsCount === 0"
          :loading="loading"
          @click="actionSelectedLinks"
        />
      </q-card-actions>
    </q-card>

    <archive-confirmation-dialog
      v-if="model.archivable"
      ref="archiveConfirmationDialog"
      hide-view
      @before-archive="beforeArchive"
      @after-archive="afterArchive"
    />
    <versions-dialog
      v-if="model.versioned"
      ref="versionsDialog"
    />
  </q-dialog>
</template>

<script>
import { Notify } from 'quasar'
import ArchivableModel from 'src/models/archivable/ArchivableModel'
import { MODEL_LEVEL } from 'src/models/model'
import ArchiveConfirmationDialog from 'src/modules/admin/components/archiving/ArchiveConfirmationDialog'
import { ACTIONS } from 'src/modules/admin/components/ModelForm/components/library/constants'
import LinkToCircuitItem from 'src/modules/admin/components/ModelForm/components/library/LinkToCircuitItem'
import LinkToModelItem from 'src/modules/admin/components/ModelForm/components/library/LinkToModelItem'
import LinkToSiteItem from 'src/modules/admin/components/ModelForm/components/library/LinkToSiteItem'
import ManageLinksActionsSelect from 'src/modules/admin/components/ModelForm/components/library/ManageLinksActionsSelect'
import VersionsDialog from 'src/modules/admin/versions/VersionsDialog'

export default {
  name: 'ManageLinksDialog',
  configuration: ['currentCircuit', 'currentSite'],
  context: ['currentUser'],
  components: {
    LinkToCircuitItem,
    LinkToSiteItem,
    LinkToModelItem,
    VersionsDialog,
    ArchiveConfirmationDialog,
    ManageLinksActionsSelect
  },
  models: {
    librariableLinkable: {
      variables () {
        return {
          modelClass: this.model.typeName,
          modelId: this.model.id,
          skip: _.isNil(this.model) || this.model.isLocal
        }
      }
    }
  },
  props: {
    modelValue: {
      type: Boolean,
      required: false
    },
    model: {
      type: Object,
      required: false,
      default: null
    }
  },
  emits: ['update:model-value'],
  data () {
    return {
      shown: false,
      action: ACTIONS.LINK,
      expandedCircuitsByIds: {},
      selectedDescendantsByIds: {},
      loading: false,
      linkingDestinationName: '',
      linkingDestinationIndex: 0,
      linkingDestinationTotal: 0
    }
  },
  computed: {
    actionLabel () {
      switch (this.action) {
        case ACTIONS.LINK:
          return `${_.startCase(this.action)} to ${this.selectedDescendantsCount} ${this.model.level === MODEL_LEVEL.SITE ? 'sites' : 'circuits'}`
        case ACTIONS.ENABLE_UPDATES:
        case ACTIONS.DISABLE_UPDATES:
          return `${_.startCase(this.action)} on ${this.selectedDescendantsCount} links`
        default:
          return `${_.startCase(this.action)} ${this.selectedDescendantsCount} links`
      }
    },
    selectedAll () {
      return !_.some(this.selectedDestinationsByIds, (selectionData, _circuitId) => !selectionData.selected)
    },
    selectedDescendantsCount () {
      return this.selectedDescendants.length
    },
    selectedDescendants () {
      const selectedDescendants = []

      for (const circuitId in this.selectedDescendantsByIds) {
        for (const siteId in this.selectedDescendantsByIds[circuitId]) {
          for (const descendantId in this.selectedDescendantsByIds[circuitId][siteId]) {
            if (this.selectedDescendantsByIds[circuitId][siteId][descendantId]) {
              const descendant = this.descendantsBySiteByCircuit[circuitId].sites[siteId].descendants[descendantId]
              if (this.descendantSelectable(descendant)) {
                selectedDescendants.push(descendant)
              }
            }
          }
        }
      }

      return selectedDescendants
    },
    selectedDestinationsByIds () {
      const selectedDestinationsByIds = {}
      for (const circuitId in this.selectedDescendantsByIds) {
        selectedDestinationsByIds[circuitId] = {
          // Default to true, if any site is unselected, will update to false
          selected: true,
          sites: {}
        }
        for (const siteId in this.selectedDescendantsByIds[circuitId]) {
          const isSiteSelected = !_.some(this.selectedDescendantsByIds[circuitId][siteId], (selected, descendantId) =>
            !selected && this.descendantSelectable(this.descendantsBySiteByCircuit[circuitId].sites[siteId].descendants[descendantId])
          )
          selectedDestinationsByIds[circuitId].sites[siteId] = isSiteSelected

          if (!isSiteSelected) {
            selectedDestinationsByIds[circuitId].selected = false
          }
        }
      }
      return selectedDestinationsByIds
    },
    circuitSitesForLibrary () {
      if (!this.currentSite.isLibrary) {
        return {}
      }

      const authorizedSites = _.sortBy(this.currentUser.authorizedSites.data, site => site.isLibrary ? 0 : site.id)
      let sites = []
      if (this.currentCircuit.isLibrary) {
        if (this.model.level === MODEL_LEVEL.CIRCUIT) {
          sites = _.compact(_.map(this.currentUser.authorizedCircuits.data, circuit => {
            if (circuit.id === this.currentCircuit.id) {
              return null
            }
            return _.find(authorizedSites, site => site.circuit.id === circuit.id)
          }))
        } else {
          sites = _.compact(_.map(authorizedSites, site => {
            if (site.id === this.currentSite.id) {
              return null
            }
            return site
          }))
        }
      } else {
        sites = _.compact(_.map(authorizedSites, site => {
          if (site.id === this.currentSite.id) {
            return null
          }
          if (this.currentCircuit.id !== site.circuit.id) {
            return null
          }
          return site
        }))
      }

      // Group sites by circuit and create the desired structure
      const groupedByCircuit = _.groupBy(sites, 'circuit.id')

      return _.mapValues(groupedByCircuit, (circuitSites, circuitId) => {
        const firstSite = _.first(circuitSites)
        const transformedSites = _.keyBy(_.map(circuitSites, site => ({
          id: site.id,
          name: site.name,
          isLibrary: site.isLibrary,
          circuit: {
            id: Number(circuitId),
            name: firstSite.circuit.name
          },
          descendants: {
            null: {
              id: null,
              label: null,
              archivable: false,
              isArchived: false,
              receiveLibraryUpdates: false,
              site: {
                id: site.id,
                name: site.name,
                isLibrary: site.isLibrary,
                circuit: {
                  id: Number(circuitId),
                  name: firstSite.circuit.name
                }
              }
            }
          }
        })), 'id')

        return {
          id: Number(circuitId),
          name: firstSite.circuit.name,
          sites: transformedSites
        }
      })
    },
    descendantsBySiteByCircuit () {
      // Start with a deep clone of libraryDescendants
      const descendantsBySiteByCircuit = _.cloneDeep(this.librariableLinkable.libraryDescendants || {})

      // Merge in missing circuits and sites from circuitSitesForLibrary
      _.each(this.circuitSitesForLibrary, (circuitData, circuitId) => {
        // If circuit doesn't exist in modelsBySiteByCircuit, add it
        if (!descendantsBySiteByCircuit[circuitId]) {
          descendantsBySiteByCircuit[circuitId] = {
            id: circuitData.id,
            name: circuitData.name,
            sites: circuitData.sites
          }
        } else {
          // Merge in missing sites from circuitSitesForLibrary
          _.each(circuitData.sites, (siteData, siteId) => {
            if (!descendantsBySiteByCircuit[circuitId].sites[siteId]) {
              descendantsBySiteByCircuit[circuitId].sites[siteId] = {
                id: siteData.id,
                name: siteData.name,
                isLibrary: siteData.isLibrary,
                circuit: siteData.circuit,
                descendants: {
                  null: {
                    id: null,
                    label: null,
                    archivable: false,
                    isArchived: false,
                    receiveLibraryUpdates: false,
                    site: {
                      id: siteData.id,
                      name: siteData.name,
                      isLibrary: siteData.isLibrary,
                      circuit: siteData.circuit
                    }
                  }
                }
              }
            }
          })
        }
      })

      return descendantsBySiteByCircuit
    },
    circuitsSitesDescendants () {
      return _.sortBy(_.map(this.descendantsBySiteByCircuit, (circuit, _circuitId) => ({
        ...circuit,
        sites: _.sortBy(_.map(circuit.sites, (site, _siteId) => ({
          ...site,
          descendants: _.map(site.descendants, (descendant, _descendantId) => ({
            ...descendant
          }))
        })), site => { return site.isLibrary ? '0' : site.name })
      })), 'name')
    }
  },
  watch: {
    modelValue () {
      if (this.modelValue) {
        this.show()
      } else {
        this.hide()
      }
    },
    shown () {
      this.$emit('update:model-value', this.shown)
    },
    action () {
      this.initialize()
    }
  },
  created () {
    _.extend(this, { MODEL_LEVEL })
  },
  methods: {
    show () {
      this.action = ACTIONS.LINK
      this.initialize()
      this.shown = true
    },
    hide () {
      this.shown = false
    },
    initialize () {
      const defaultExpanded = this.circuitsSitesDescendants.length === 1
      this.expandedCircuitsByIds = {}
      this.selectedDescendantsByIds = {}
      for (const circuitId in this.descendantsBySiteByCircuit) {
        this.expandedCircuitsByIds[circuitId] = defaultExpanded
        this.selectedDescendantsByIds[circuitId] = {}
        for (const siteId in this.descendantsBySiteByCircuit[circuitId].sites) {
          this.selectedDescendantsByIds[circuitId][siteId] = {}
          for (const descendantId in this.descendantsBySiteByCircuit[circuitId].sites[siteId].descendants) {
            const descendant = this.descendantsBySiteByCircuit[circuitId].sites[siteId].descendants[descendantId]
            // For a model to be selected for a non-LINK action, it must exist (has a valid id)
            // All selectable models default to unselected, as they can be actioned on selection
            // All non-selectable models default to selected, as they are effectively already actioned
            // i.e. Any model with update enabled is effectively already actioned by ENABLE_UPDATES
            const isDescendantSelected = (this.action === ACTIONS.LINK || !_.isNil(descendant?.id)) && !this.descendantSelectable(descendant)
            this.selectedDescendantsByIds[circuitId][siteId][descendantId] = isDescendantSelected
          }
        }
      }
    },
    circuitSelectable (circuit) {
      return _.some(circuit.sites, site => this.siteSelectable(site))
    },
    siteSelectable (site) {
      return _.some(site.descendants, descendant => this.descendantSelectable(descendant))
    },
    descendantSelectable (descendant) {
      switch (this.action) {
        case ACTIONS.LINK:
          return _.isNil(descendant?.id)
        case ACTIONS.ENABLE_UPDATES:
          return !_.isNil(descendant?.id) && !descendant.receiveLibraryUpdates
        case ACTIONS.DISABLE_UPDATES:
          return !_.isNil(descendant?.id) && descendant.receiveLibraryUpdates
        case ACTIONS.FORCE_UPDATE:
          return !_.isNil(descendant?.id)
        case ACTIONS.ARCHIVE:
          return !_.isNil(descendant?.id) && descendant.archivable && !descendant.isArchived
        case ACTIONS.UNARCHIVE:
          return !_.isNil(descendant?.id) && descendant.archivable && descendant.isArchived
        case ACTIONS.UNLINK:
          return !_.isNil(descendant?.id)
        default:
          return false
      }
    },
    toggleCircuit (circuit, toggleValue = !this.selectedDestinationsByIds[circuit.id].selected) {
      for (const siteId in this.selectedDescendantsByIds[circuit.id]) {
        this.toggleSite(this.descendantsBySiteByCircuit[circuit.id].sites[siteId], toggleValue)
      }
    },
    toggleSite (site, toggleValue = !this.selectedDestinationsByIds[site.circuit.id].sites[site.id]) {
      for (const descendantId in this.selectedDescendantsByIds[site.circuit.id][site.id]) {
        const descendant = this.descendantsBySiteByCircuit[site.circuit.id].sites[site.id].descendants[descendantId]
        if (this.descendantSelectable(descendant)) {
          this.toggleDescendant(descendant, toggleValue)
        }
      }
    },
    toggleDescendant (descendant, toggleValue = !this.selectedDescendantsByIds[descendant.site.circuit.id][descendant.site.id][descendant?.id]) {
      this.selectedDescendantsByIds[descendant.site.circuit.id][descendant.site.id][descendant?.id] = toggleValue
    },
    selectAll () {
      const toggleValue = !this.selectedAll
      for (const circuit of this.circuitsSitesDescendants) {
        this.toggleCircuit(circuit, toggleValue)
      }
    },
    selectedDestinations () {
      if (this.model.level === MODEL_LEVEL.SITE) {
        return _(this.selectedDestinationsByIds).map((selectionData, circuitId) => {
          return _.map(selectionData.sites, (selected, siteId) => {
            const site = this.descendantsBySiteByCircuit[circuitId].sites[siteId]
            if (selected && this.siteSelectable(site)) {
              return site
            } else {
              return null
            }
          })
        }).flatten().compact().value()
      } else {
        return _(this.selectedDestinationsByIds).map((selectionData, circuitId) => {
          const circuit = this.descendantsBySiteByCircuit[circuitId]
          if (selectionData.selected && this.circuitSelectable(circuit)) {
            return circuit
          } else {
            return null
          }
        }).compact().value()
      }
    },
    async linkSelectedDestinations () {
      const destinations = this.selectedDestinations()

      this.linkingDestinationName = ''
      this.linkingDestinationIndex = 0
      this.linkingDestinationTotal = destinations.length

      try {
        this.loading = true

        const allErrors = []
        // We cannot use await within _.each
        for (const destination of destinations) {
          this.linkingDestinationName = destination.name
          this.linkingDestinationIndex++
          const destinationSiteId = this.model.level === MODEL_LEVEL.SITE ? destination.id : _.first(_.values(destination.sites)).id

          const { errors: duplicationErrors } = await this.model.duplicateModelToSite(this.model.id, destinationSiteId)
          allErrors.push(duplicationErrors)
        }

        if (!_.isEmpty(_.flatten(allErrors))) {
          Notify.create({
            message: 'Error creating links',
            type: 'negative',
            position: 'top'
          })
        }
      } finally {
        this.linkingDestinationName = ''
        this.linkingDestinationIndex = 0
        this.linkingDestinationTotal = 0
        await this.librariableLinkable.fetch(true)
        this.initialize()
        this.loading = false
      }
    },
    beforeArchive (_modelId) {
      this.loading = true
    },
    async afterArchive (_modelId) {
      await this.librariableLinkable.fetch(true)
      this.initialize()
      this.loading = false
    },
    async unarchiveDescendant (descendant) {
      this.$q.dialog({
        title: 'Confirm Unarchive',
        message: `Are you sure you want to unarchive ${this.model.name} (ID ${descendant.id})?`,
        ok: {
          label: 'Unarchive',
          color: 'primary',
          autofocus: true
        },
        cancel: {
          label: 'Cancel',
          color: 'secondary'
        },
        focus: 'cancel'
      }).onOk(async () => {
        try {
          this.loading = true
          const results = await ArchivableModel.unarchiveModel(this.model.typeName, descendant.id)
          if (!_.isEmpty(results?.errors)) {
            this.$mh.showErrors(results.errors)
          }
          await this.librariableLinkable.fetch(true)
          this.initialize()
        } finally {
          this.loading = false
        }
      })
    },
    async actionSelectedLinks () {
      if (this.selectedDescendantsCount === 0) return

      if (this.action === ACTIONS.LINK) return await this.linkSelectedDestinations()

      this.linkingDestinationName = ''
      this.linkingDestinationIndex = 0
      this.linkingDestinationTotal = this.selectedDescendants.length

      try {
        this.loading = true
        const allErrors = []
        // We cannot use await within _.each
        for (const descendant of this.selectedDescendants) {
          if (this.model.level === MODEL_LEVEL.SITE) {
            this.linkingDestinationName = descendant.site.name
          } else {
            this.linkingDestinationName = descendant.site.circuit.name
          }
          this.linkingDestinationIndex++
          switch (this.action) {
            case ACTIONS.ENABLE_UPDATES: {
              const { errors: enableErrors } = await this.librariableLinkable.enableLibrariable(this.model.typeName, descendant.id)
              allErrors.push(enableErrors)
              break
            }
            case ACTIONS.DISABLE_UPDATES: {
              const { errors: disableErrors } = await this.librariableLinkable.disableLibrariable(this.model.typeName, descendant.id)
              allErrors.push(disableErrors)
              break
            }
            case ACTIONS.FORCE_UPDATE: {
              const { errors: syncErrors } = await this.librariableLinkable.syncLibrariable(this.model.typeName, descendant.id)
              allErrors.push(syncErrors)
              break
            }
            case ACTIONS.ARCHIVE: {
              const { errors: archiveErrors } = await this.$refs.archiveConfirmationDialog.archiveModel(this.model.typeName, descendant.id, false)
              allErrors.push(archiveErrors)
              break
            }
            case ACTIONS.UNARCHIVE: {
              const { errors: unarchiveErrors } = await this.$refs.archiveConfirmationDialog.unarchiveModel(this.model.typeName, descendant.id)
              allErrors.push(unarchiveErrors)
              break
            }
            case ACTIONS.UNLINK: {
              const { errors: unlinkErrors } = await this.librariableLinkable.deleteLibrariable(this.model.typeName, descendant.id)
              allErrors.push(unlinkErrors)
              break
            }
          }
        }

        if (!_.isEmpty(_.flatten(allErrors))) {
          Notify.create({
            message: 'Error updating links',
            type: 'negative',
            position: 'top'
          })
        }
      } finally {
        this.linkingDestinationName = ''
        this.linkingDestinationIndex = 0
        this.linkingDestinationTotal = 0
        await this.librariableLinkable.fetch(true)
        this.initialize()
        this.loading = false
      }
    },
    async refresh () {
      try {
        this.loading = true
        await this.librariableLinkable.fetch(true)
        this.initialize()
      } finally {
        this.loading = false
      }
    }
  }
}

</script>

<style lang="scss" scoped>
.action-select {
  :deep(.q-field__control) {
    border-radius: 6px;
  }
  box-shadow: 0 2px 0 0 rgba(10, 13, 18, 0.04);
}

.q-checkbox {
  :deep(.q-checkbox__bg) {
    border-radius: 6px;
  }
  :deep(.q-checkbox__svg) {
    width: 75% !important;
    height: 75% !important;
    margin: auto;
  }
}

.link-action-button {
  max-width: 40%;
  min-width: 150px;
  border-radius: 8px;
  font-size: 1rem;
}

.q-card {
  .q-card-section {
    &.flex-grow-1 {
      flex: 1 1 auto;
    }
    &.flex-shrink-0 {
      flex: 0 0 auto;
    }
  }
}
</style>
